import { IRestLayerRequest } from "@gtmhub/core";
import { IEntity } from "@gtmhub/entities/models/models";
import { IInsightSource, IInsightState, IInsightboardItemSetting, IInsightboard } from "@gtmhub/insightboards/models/models";
import { IUnitFormatting } from "@gtmhub/shared/models";
import { IKpi } from "@gtmhub/kpis";
import { ILoggable } from "@gtmhub/core/logging";
import { IMetric } from "@gtmhub/okrs/metrics";

export interface IInsightBlueprint {
  // id (primary key) of the insight blueprint
  id: string;
  // name (has to be unique within a store) of the insight blueprint;
  // this is what we use in the code to identify the insight blueprint
  name: string;
  // user friendly title of the insight blueprint; this is what we show
  // in the UI
  title: string;
  // the description of the insight blueprint
  description: string;
  // id of the category to which the insight belongs to
  categoryId: string;
  // an array of entity blueprint names that represent required entities
  // of this blueprint
  requiredEntities: IEntity[];
  status: string;
  view: string;
  algorithm: string;
}

export interface IInsightSqlConditionOperators {
  fieldType: string;
  operators: string[];
}

export interface IInsightFieldTypes {
  integer: string;
  long: string;
  double: string;
  date: string;
  string: string;
  timestamp: string;
  bool: string;
  dateTime: string;
  unixTime: string;
  arrayStrings: string;
}

export interface ISqlOperatorsMap {
  [operator: string]: string;
}

export enum SupportedDataType {
  Integer = "integer",
  Long = "long",
  Double = "double",
  Date = "date",
  String = "string",
  Timestamp = "timestamp",
  Bool = "bool",
  DateTime = "dateTime",
  UnixTime = "unixTime",
  ArrayStrings = "[]string",
}

export enum SupportedOperators {
  Equals = "equals",
  NotEqual = "notEqual",
  Contains = "contains",
  DoesNotContain = "doesNotContain",
  IsContainedBy = "isContainedBy",
  IsNotContainedBy = "isNotContainedBy",
  Overlaps = "overlaps",
  DoesNotOverlap = "doesNotOverlap",
  HasLengthOf = "hasLengthOf",
  Greater = "greater",
  Less = "less",
  Longer = "longer",
  Shorter = "shorter",
}

export interface ISqlCondition {
  value: string;
  dataType: SupportedDataType;
  strOperator: SupportedOperators;
  title: string;
  field: string;
  sqlOperators: string[];
  isSingleValue?: boolean;
}

export interface IDataSourceEntity {
  dataSource: string;
  dataSourceImg: string;
  dataSourceLogoUrl?: string;
  dataSourceTitle: string;
  aggregatedTitle: string;
  entity: string;
  entityTitle: string;
  connectionData?: IConnectionData;
}

export interface IDataSourceEntitiesMap {
  [dataSource: string]: IDataSourceMapping;
}

export interface IDataSourceMapping {
  dataSource: string;
  dataSourceTitle: string;
  dataSourceLogoUrl?: string;
  showEntities: boolean;
  entities: IDataSourceEntity[];
  connections: IConnectionData[];
}

export type ConditionalOperator = "AND" | "OR";
export interface IInsightQueryCondition {
  title: string;
  dataType: SupportedDataType;
  value: any; // could be literally anything
  bluredValue: any; // could be literally anything
  templateFunc?(condition): string;
  strOperator: SupportedOperators;
  field: string;
  nextConditionBooleanLogic?: ConditionalOperator;
  nextCondition?: IInsightQueryCondition;
  sqlOperators?: string[];
  isSingleValue?: boolean;
}
export interface IAggregationField {
  name: string;
  title: string;
  type: string;
}

export enum SqlFunction {
  Count = "count",
  Sum = "sum",
  Avg = "avg",
  Min = "min",
  Max = "max",
}

export interface IInsightQueryLayout {
  sqlFunction: SqlFunction;
  dataSource: string;
  dataSourceImg: string;
  dataSourceLogoUrl?: string;
  entity: string;
  entityTitle: string;
  aggregatedField?: IAggregationField;
  conditions?: IInsightQueryCondition;
}

export interface IInsight {
  id: string;
  // shows if currently there is a process going on with the given insight
  processing: any;
  accountId: string;
  name: string;
  title: string;
  description: string;
  categoryId: string;
  requiredEntities: IEntity[];
  status: string;

  createdById?: string;
  modifiedById?: string;
  totalCount?: number;
  source?: IInsightSource;

  view: string;

  formatting?: IInsightFormatting;

  queryLayout?: IInsightQueryLayout;

  language: string;
  algorithm: string;
  tags?: any;
  filters?: IInsightFilters;
  size?: {
    width: number;
    height: number;
  };
  direct?: boolean;
  isScalar?: boolean;
  settings?: IInsightSetting[];
  usedFilters?: string[];
  usedEntities?: IInsightUsedEntity[];

  // ui
  newTag?: string;
  metrics?: any;
  insightboards?: Partial<IInsightboard>[];
  kpis?: Partial<IKpi>[];
  isSelected?: boolean;
  createdBy?: any;
  modifiedBy?: any;
  state?: IInsightState;
  submitEmailContent?: ISubmitInsightEmailContent;
  connection?: IConnectionData;
  isShadow?: boolean;
}

export interface IMarketplaceInsightTemplate {
  id: string;
  title: string;
  description: string;
  installsCount: number;
  showInList: boolean;
  requiredEntities?: {
    connector: string;
  }[];
  semanticSimilarityScore?: number;
}

export interface IMarketplaceInsightSuggestedTemplate {
  items: IMarketplaceInsightTemplate[];
  semanticSimilarityScore: number;
  totalCount: number;
  predictionId: string;
}

export interface IConnectionData {
  id?: string;
  name: string;
  title: string;
  logoUrl?: string;
}

export interface IInsightFormatting {
  color?: string;
  editor?: string;
  decimalSize?: number | string;
  metadata?: IInsightMetadata;
  thousandsSeparator?: boolean;
  unit?: string;
  isCustomUnit?: boolean;
  suffix?: boolean;
  uiInvalid?: boolean;
  editSettings?: boolean;
  isCodeless?: boolean;
}

export interface IInsightMetadata {
  [key: string]: number | string;
}

export interface IInsightDefaultData {
  [key: string]: number | string;
}

export interface IUpdateInsight {
  id: string;
  accountId?: string;
  name?: string;
  title?: string;
  description?: string;
  categoryId?: string;
  requiredEntities?: IEntity[];
  status?: string;

  createdById?: string;
  modifiedById?: string;
  totalCount?: number;
  source?: IInsightSource;

  view?: string;

  formatting?: IInsightFormatting | Record<string, unknown>;

  language?: string;
  algorithm?: string;
  tags?: any;
  filters?: IInsightFilters | Record<string, unknown>;
  size?: {
    width: number;
    height: number;
  };
  direct?: boolean;
  isScalar?: boolean;
  settings?: IInsightSetting[];
  usedFilters?: string[];
}

export interface IInsightFilters {
  [filterKey: string]: string;
}

export interface ICreateKrKpiFilter {
  name: string;
  value: string;
  valueDisplayName?: string;
}

export interface IInsightSetting {
  name: string;
  title: string;
  description: string;
  type: string;
  value?: string;
}

export interface ISnippet {
  name: string;
  code: string;
  language: string;
}

export interface IInsightCalculationPayload {
  language: string;
  script: string;
  filters: IInsightFilters;
  settings: IInsightboardItemSetting[];
  id?: string;
}

export interface IInsightMetricUsageMap {
  [insightId: string]: IInsightMetricUsage[];
}

export interface IInsightMetricUsage {
  actual: number;
  format: IUnitFormatting;
  goal: IInsightMetricUsageGoal;
  goalId: string;
  id: string;
  initialValue: number;
  manualType: string;
  name: string;
  ownerIds: string[];
  sessionId: string;
  target: number;
  targetOperator: string;
}

export interface IInsightMetricUsageGoal {
  name: string;
  ownerIds: string[];
}

export interface IInsightCalculationTransaction {
  transactionId: string;
}

export interface IInsightsQueryParams {
  categoryId?: string;
  insightIds?: string;
  fields?: string;
  skip?: number;
  take?: number;
  stringSearch?: string;
  sort?: string;
  isScalar?: boolean;
  usedConnectors?: string[];
}

export interface IInsightChangeLogs {
  totalCount: number;
  items: IInsightChangeLog[];
}

export interface IInsightChangeLog {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _id: string;
  createdBy: string;
  created: string;
  version: string;
  changeLogItems: IInsightChangeLogItem[];
  metadata: IInsightChangeLogMetadata;
}

export interface IInsightChangeLogItem {
  diffs: {
    oldValue: string;
    newValue: string;
  };
  field: string;
}

export interface IInsightChangeLogMetadata {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  insight_filters: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  insight_formatting: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  insight_view?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  insight_algorithm?: string;
}

export interface ISubmitInsightEmailContent {
  mailto: string;
  subject: string;
  body: string;
}

export interface IInsightUsedEntity {
  name: string;
  dataSourceBlueprintName: string;
  dataSourceBlueprintLogoUrl?: string;
  remoteEntityName: string;
  requiredFields: IInsightEntityRequiredField[];
}

export interface IInsightEntityRequiredField {
  name: string;
}

export interface IDirectSubmitInsight {
  title: string;
  description: string;
  view: string;
  language: string;
  algorithm: string;
  isScalar: boolean;
  hidden?: boolean;
}

export interface IGetMarketplaceInsightTemplatesQuery extends IRestLayerRequest {
  take?: number;
  text?: string;
  tags?: string;
  insightTemplateNames?: string;
  insightTemplateIds?: string;
  connectors?: string;
  orderBy?: string;
}

export interface IMarketplaceInsightTemplateInstallRequest {
  dashboardId?: string;
  insightTemplateId: string;
  dashboardName?: string;
}

export interface IMarketplaceInsightTemplateInstallResponse {
  createdDashboardIds: string[];
  installedInsightsIds: string[];
}

export class InsightExecutionError extends Error implements ILoggable {
  constructor(public insightId: string, public originalError: Error) {
    super(`Insight threw an error: ${insightId} -> ${originalError.message}`);

    this.name = "InsightExecutionError";
  }

  loggableData(): Record<string, unknown> {
    return {
      insight: true,
    };
  }
}

export interface IInsightUsage {
  insightboards: IInsightboard[];
  kpis: IKpi[];
  metrics: IMetric[];
}
